import React, { useContext } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { Typography } from '@mui/material';

import { storeContext } from '../../../../context';
import { t } from '../../../../../utils/i18next';
import CurrencyFormat from '../../../general/CurrencyFormat';
import { spacingInRems } from '../../../../theme/themeUtils';
import { isNumber } from '../../../../../utils/Number';
import { dateInUTC } from '../../../../../utils/Date';
import ServiceFeeTooltip from './OfferItem/ServiceFeeTooltip';

const translationsBase = 'payables.payment_options';

const StyledContainer = styled.div`
  && {
    && {
      padding-top: 0;
      display: flex;
      flex-direction: column;
      .container {
        display: flex;
        width: 100%;
        justify-content: space-between;
        margin-top: ${spacingInRems(0.5)};
        &.message {
          margin-top: 0;
          &.center {
            justify-content: center;
          }
        }
      }
      span.date {
        white-space: nowrap;
      }
    }
  }
`;

/**
 * Displays balance info, such as next auto debit date, or balance outside of plan
 */

const PaymentBalanceInfo = observer(() => {
  const {
    payable,
    translations: { currentLanguage }
  } = useContext(storeContext);

  const {
    paymentPlan,
    hasActivePaymentPlan,
    standingAmount,
    appointmentsAmountOutOfPlan,
    appointmentsTotalBalance
  } = payable;

  if (!hasActivePaymentPlan) {
    console.log('no payment plan found');
    return null;
  }

  const {
    planType,
    amountOutOfPlan: ppAmountOutOfPlan,
    paymentMethod,
    nextInstallment,
    isDelinquent,
    isGoodStanding
  } = paymentPlan;

  const amountOutOfPlan = ppAmountOutOfPlan + appointmentsAmountOutOfPlan;

  const elements = [];

  const hasValidNextPayment = () =>
    !!(
      nextInstallment &&
      isNumber(nextInstallment.amountToPay) &&
      nextInstallment.installmentDate
    );

  //logic is taken from apps/patient/app/views/payables/_amount_due_v2.haml
  if (isDelinquent || isGoodStanding) {
    elements.push(
      <div
        className={'container message center'}
        key={1}
        data-testid="delinquentMessage"
      >
        <Typography variant="subtitle2">
          {t(`${translationsBase}.message.${planType}_plan_on_delinquent`)}
          <span>&nbsp;-&nbsp;</span>
        </Typography>

        <Typography
          variant="subtitle2"
          color={isDelinquent ? 'error' : 'primary'}
        >
          <strong>
            {t(
              `${translationsBase}.message.${
                isDelinquent ? 'delinquent' : 'good_standing'
              }`
            )}
          </strong>
        </Typography>
      </div>
    );
  }
  if (amountOutOfPlan > 0) {
    elements.push(
      <div
        className={'container message center'}
        key={2}
        data-testid="amountOutOfPlanMessage"
      >
        <Typography variant="subtitle2">
          <span>{t(`${translationsBase}.message.balance_not_on_plan`)}:</span>
          <span>&nbsp;</span>
          <strong>
            <CurrencyFormat value={amountOutOfPlan} />
          </strong>
        </Typography>
      </div>
    );
  } else if (!paymentMethod && hasValidNextPayment()) {
    elements.push(
      <div
        className={'container message center'}
        key={3}
        data-testid="nextAutoDebitMessage"
      >
        <Typography variant="subtitle2">
          <span>
            {t(`${translationsBase}.message.next_${planType}_plan_payment`)}
          </span>
          <span className="date">
            {dateInUTC(nextInstallment.installmentDate)}
          </span>
        </Typography>
      </div>
    );
  }

  if (
    payable.aNextPayment > 0 &&
    hasValidNextPayment() &&
    (paymentMethod || isDelinquent || amountOutOfPlan > 0)
  ) {
    elements.push(
      <div className={'container'} key={4} data-testid="nextPayment">
        <Typography variant="body1">
          <span>
            {t(`${translationsBase}.message.next_${planType}_plan_payment`)}
          </span>
          <span className="date">
            {dateInUTC(nextInstallment.installmentDate)}
          </span>
        </Typography>
        <Typography variant="body1">
          <CurrencyFormat
            value={
              nextInstallment.amountToPayWithFee || nextInstallment.amountToPay
            }
          />
        </Typography>
      </div>
    );
  } else {
    elements.push(
      <Typography
        variant="body1"
        className={'container'}
        key={5}
        data-testid="paymentPlan"
      >
        {t(`${translationsBase}.${planType}_plan_on`)}
      </Typography>
    );
  }

  elements.push(
    <Typography
      variant="body1"
      className={'container'}
      key={6}
      data-testid="balanceRemaining"
    >
      <span>{t(`${translationsBase}.payment.balance`)}</span>
      <CurrencyFormat
        value={Math.max(standingAmount + appointmentsTotalBalance, 0)}
      />
    </Typography>
  );

  if (amountOutOfPlan > 0 && isDelinquent) {
    elements.push(
      <Typography
        variant="body1"
        className={'container'}
        key={7}
        data-testid="balanceNotOnPlan"
      >
        <span>{t(`${translationsBase}.balance_not_on_plan`)}</span>
        <CurrencyFormat value={amountOutOfPlan} />
      </Typography>
    );
  }

  if (paymentPlan.serviceFeeAmount) {
    elements.push(
      <ServiceFeeTooltip serviceFee={paymentPlan.serviceFeeAmount} />
    );
  }

  return <StyledContainer>{elements}</StyledContainer>;
});

export default PaymentBalanceInfo;
